/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Minimalist Zen Writer Layout Template (Task 4.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../context/ThemeProvider';
import { useAuthStore } from '../../stores/useAuthStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { useUiStore } from '../../stores/useUiStore';
import { resolvePlatformDomains } from '@sofia/shared-types';
import { LOGO_VARIANTS } from '@sofia/assets';
import { NAVIGATION_SECTIONS } from '../../navigation/navigation-items';
import { WorkspaceSwitcher } from '../../components/workspace/WorkspaceSwitcher';
import { UserProfileDropdown } from '../../components/header/UserProfileDropdown';
import { WalletBadge } from '../../components/wallet/WalletBadge';
import { LanguageSwitcher } from '../../components/i18n/LanguageSwitcher';
import { ThemeSwitcher } from '../../components/theme/ThemeSwitcher';
import { CreateWorkspaceModal } from '../../components/workspace/CreateWorkspaceModal';
import { CommandPalette } from '../../components/command/CommandPalette';
import { TopupModal } from '../../components/wallet/TopupModal';
import { PaymentCheckoutModal } from '../../components/billing/PaymentCheckoutModal';
import { InviteMemberModal } from '../../components/team/InviteMemberModal';
import { MobileDrawer } from '../../components/navigation/MobileDrawer';
import {
  Menu,
  Search,
  Maximize2,
  Minimize2,
  Pin,
  PinOff,
  Feather,
  ArrowLeft,
  Minimize,
  Maximize,
} from 'lucide-react';
import type { DashboardLayoutProps } from '../../layouts/DashboardLayout';

export const MinimalZenLayout: React.FC<DashboardLayoutProps> = ({
  currentPath,
  onNavigate,
  children,
}) => {
  const { themeData, isDark } = useTheme();
  const { user, clearSession } = useAuthStore();
  const { t } = useI18nStore();
  const { setMobileDrawerOpen, openCommandPalette } = useUiStore();

  const mainContainerRef = useRef<HTMLElement>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isHeaderPinned, setIsHeaderPinned] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFocusWidth, setIsFocusWidth] = useState(true);
  const [wordCount, setWordCount] = useState(0);

  const branding = themeData?.branding;
  const siteName = branding?.siteName || 'Sophia Platform';
  const defaultLogo = isDark ? LOGO_VARIANTS.fullDark : LOGO_VARIANTS.fullLight;
  const logoUrl =
    (isDark ? branding?.logoDarkUrl : branding?.logoLightUrl) ||
    branding?.logoLightUrl ||
    branding?.logoDarkUrl ||
    defaultLogo;

  const handleExitImpersonation = () => {
    clearSession();
    const domains = resolvePlatformDomains();
    window.location.href = domains.adminOrigin;
  };

  // Find active navigation item and section
  const activeNav = useMemo(() => {
    for (const section of NAVIGATION_SECTIONS) {
      const foundItem = section.items.find((item) => item.path === currentPath);
      if (foundItem) {
        return { section, item: foundItem };
      }
    }
    return {
      section: NAVIGATION_SECTIONS[0],
      item: NAVIGATION_SECTIONS[0]?.items[0],
    };
  }, [currentPath]);

  // Synchronize document title and favicon
  useEffect(() => {
    const pageTitle = activeNav?.item?.label ?? 'Zen Writer';
    document.title = `${pageTitle} | ${siteName} (${t('zen.zenMode')})`;

    if (themeData?.branding?.faviconUrl) {
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0]?.appendChild(link);
      }
      link.href = themeData.branding.faviconUrl;
    }
  }, [activeNav, siteName, themeData, t]);

  // Fullscreen state listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  }, []);

  // Keyboard shortcut listener (Esc / Cmd+Shift+F)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+Shift+F or Ctrl+Shift+F: Toggle Fullscreen
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        toggleFullscreen();
        return;
      }

      // Escape: Exit Fullscreen or reveal header
      if (event.key === 'Escape') {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        } else {
          setIsHeaderVisible(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleFullscreen]);

  // Auto-hide header on scroll (unless pinned)
  useEffect(() => {
    if (isHeaderPinned) {
      setIsHeaderVisible(true);
      return;
    }

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        // Scrolling down -> hide header
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up -> show header
        setIsHeaderVisible(true);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHeaderPinned]);

  // Reveal header when mouse hovers near the top of the viewport
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isHeaderPinned) return;
      if (event.clientY < 60) {
        setIsHeaderVisible(true);
      }
    },
    [isHeaderPinned]
  );

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  // Word count & Reading time calculation
  useEffect(() => {
    const calculateStats = () => {
      if (!mainContainerRef.current) return;
      const text = mainContainerRef.current.innerText || '';
      const words = text
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0).length;
      setWordCount(words);
    };

    calculateStats();

    // Observe content mutations
    const observer = new MutationObserver(() => {
      calculateStats();
    });

    if (mainContainerRef.current) {
      observer.observe(mainContainerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [children, currentPath]);

  const readingMinutes = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div className="template-zen min-h-screen bg-background-base flex flex-col font-sans text-foreground transition-colors duration-200 relative selection:bg-primary/20 selection:text-primary">
      {/* 0. Impersonation Top Amber Banner */}
      {user?.isImpersonated && (
        <aside
          aria-label="Impersonation Warning Banner"
          className="bg-amber-500 text-amber-950 px-4 py-2 text-xs font-semibold flex items-center justify-between sticky top-0 z-50 shadow-md border-b border-amber-600/30 shrink-0"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm">⚠️</span>
            <span>{t('impersonation.bannerNotice', { email: user.email })}</span>
          </div>
          <button
            type="button"
            onClick={handleExitImpersonation}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-950 text-amber-100 hover:bg-black font-semibold text-xs transition-colors ml-4 shadow-sm"
          >
            <span>{t('impersonation.exit')}</span>
          </button>
        </aside>
      )}

      {/* Top Edge Hover Trigger Zone */}
      <div
        className="fixed top-0 inset-x-0 h-3 z-40 cursor-default"
        onMouseEnter={() => setIsHeaderVisible(true)}
        aria-hidden="true"
      />

      {/* 1. Floating Auto-Hide Zen Header */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ease-in-out px-4 sm:px-6 py-2.5 ${
          isHeaderVisible
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : '-translate-y-full opacity-0 pointer-events-none'
        }`}
        onMouseEnter={() => setIsHeaderVisible(true)}
      >
        <div className="max-w-6xl mx-auto rounded-2xl bg-surface/85 backdrop-blur-xl border border-surface-border shadow-lg px-4 py-2 flex items-center justify-between gap-3">
          {/* Left: Exit Zen / Back & Studio Label */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate('/workspace/dashboard')}
              className="p-1.5 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/70 transition-colors flex items-center gap-1.5 text-xs font-semibold"
              title={t('zen.exitZen')}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </button>

            <div className="h-4 w-px bg-surface-border hidden sm:block" />

            <div className="flex items-center gap-2">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={siteName}
                  className="h-6 max-w-[100px] object-contain hidden md:block"
                />
              ) : null}
              <span className="font-heading font-bold text-xs text-foreground flex items-center gap-1.5">
                <span>{activeNav?.item?.label}</span>
                <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                  {t('zen.zenMode')}
                </span>
              </span>
            </div>
          </div>

          {/* Center: Command Palette Search */}
          <div className="flex-1 max-w-xs mx-auto hidden md:block">
            <button
              type="button"
              onClick={openCommandPalette}
              className="w-full flex items-center justify-between px-3 py-1.5 rounded-xl border border-surface-border bg-muted/40 hover:bg-muted/70 text-xs text-foreground-muted transition-colors"
              title={t('header.searchOrRun')}
            >
              <div className="flex items-center gap-2 truncate">
                <Search className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{t('header.quickSearch')}</span>
              </div>
              <kbd className="px-1.5 py-0.2 rounded bg-surface-elevated border border-surface-border text-[9px] font-mono text-foreground-muted shrink-0">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Right: Zen Controls, Language, Theme, Profile */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Header Pin Toggle */}
            <button
              type="button"
              onClick={() => setIsHeaderPinned((prev) => !prev)}
              className={`p-1.5 rounded-xl transition-colors text-xs ${
                isHeaderPinned
                  ? 'bg-primary/10 text-primary border border-primary/20 font-semibold'
                  : 'text-foreground-muted hover:text-foreground hover:bg-muted/70'
              }`}
              title={isHeaderPinned ? t('zen.unpinHeader') : t('zen.pinHeader')}
              aria-label={isHeaderPinned ? t('zen.unpinHeader') : t('zen.pinHeader')}
            >
              {isHeaderPinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
            </button>

            {/* Focus Width Toggle */}
            <button
              type="button"
              onClick={() => setIsFocusWidth((prev) => !prev)}
              className={`p-1.5 rounded-xl transition-colors text-xs ${
                !isFocusWidth
                  ? 'bg-primary/10 text-primary border border-primary/20 font-semibold'
                  : 'text-foreground-muted hover:text-foreground hover:bg-muted/70'
              }`}
              title={isFocusWidth ? t('zen.fullWidth') : t('zen.focusWidth')}
              aria-label={isFocusWidth ? t('zen.fullWidth') : t('zen.focusWidth')}
            >
              {isFocusWidth ? <Maximize className="w-4 h-4" /> : <Minimize className="w-4 h-4" />}
            </button>

            {/* Fullscreen Toggle */}
            <button
              type="button"
              onClick={toggleFullscreen}
              className={`p-1.5 rounded-xl transition-colors text-xs ${
                isFullscreen
                  ? 'bg-primary/10 text-primary border border-primary/20 font-semibold'
                  : 'text-foreground-muted hover:text-foreground hover:bg-muted/70'
              }`}
              title={isFullscreen ? t('zen.exitFullscreen') : t('zen.fullscreen')}
              aria-label={isFullscreen ? t('zen.exitFullscreen') : t('zen.fullscreen')}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>

            <div className="h-4 w-px bg-surface-border hidden sm:block" />

            <LanguageSwitcher variant="header" />
            <ThemeSwitcher />
            <WalletBadge variant="header" />
            <WorkspaceSwitcher />
            <UserProfileDropdown onNavigate={onNavigate} />

            {/* Mobile Drawer Trigger */}
            <button
              type="button"
              onClick={() => setMobileDrawerOpen(true)}
              className="lg:hidden p-1.5 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/70 transition-colors"
              aria-label={t('header.openMenu')}
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* 2. Main Focus Content Viewport */}
      <main
        ref={mainContainerRef}
        className={`flex-1 w-full mx-auto p-4 sm:p-6 lg:p-8 pt-16 sm:pt-20 pb-24 transition-all duration-300 animate-fade-in ${
          isFocusWidth ? 'max-w-4xl' : 'max-w-[1600px]'
        }`}
      >
        {children}
      </main>

      {/* 3. Floating Zen Status Ribbon (Bottom Toolbar) */}
      <div className="fixed bottom-5 right-5 sm:right-8 z-40 flex items-center gap-2 p-1.5 rounded-2xl bg-surface/85 backdrop-blur-xl border border-surface-border shadow-lg text-xs text-foreground-muted select-none transition-all hover:bg-surface/95">
        {/* Word Count & Reading Time Badge */}
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-muted/40 font-mono text-[11px] text-foreground">
          <Feather className="w-3.5 h-3.5 text-primary shrink-0" />
          <span>{t('zen.words', { count: wordCount })}</span>
          <span className="text-surface-border">•</span>
          <span>{t('zen.readingTime', { minutes: readingMinutes })}</span>
        </div>

        {/* Quick Fullscreen Button */}
        <button
          type="button"
          onClick={toggleFullscreen}
          className="p-1.5 rounded-xl hover:bg-muted hover:text-foreground transition-colors"
          title={`${isFullscreen ? t('zen.exitFullscreen') : t('zen.fullscreen')} (Esc / Cmd+Shift+F)`}
          aria-label={isFullscreen ? t('zen.exitFullscreen') : t('zen.fullscreen')}
        >
          {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>

        {/* Quick Header Toggle */}
        <button
          type="button"
          onClick={() => setIsHeaderVisible((prev) => !prev)}
          className="p-1.5 rounded-xl hover:bg-muted hover:text-foreground transition-colors"
          title={isHeaderVisible ? t('zen.unpinHeader') : t('zen.pinHeader')}
          aria-label={isHeaderVisible ? t('zen.unpinHeader') : t('zen.pinHeader')}
        >
          {isHeaderVisible ? <Pin className="w-3.5 h-3.5 text-primary" /> : <PinOff className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* 4. Global Modals, Mobile Drawer & Command Palette */}
      <MobileDrawer currentPath={currentPath} onNavigate={onNavigate} />
      <CreateWorkspaceModal />
      <TopupModal />
      <PaymentCheckoutModal />
      <InviteMemberModal />
      <CommandPalette onNavigate={onNavigate} />
    </div>
  );
};
